import { zodResolver } from "@hookform/resolvers/zod"
import { Box, Dialog, DialogActions, DialogContent } from "@mui/material"
import type { FC } from "react"
import { FormProvider, useForm } from "react-hook-form"

import { Button } from "@/components/common/Button"
import { CancelDialog } from "@/components/common/Modals"
import DialogHeader from "@/components/common/Modals/DialogHeader"
import type { CompanyAccountingTerm } from "@/graphql/codegen/graphql"
import { COMPANY_TYPE } from "@/screens/Companies/constants"
import { PaymentTermsForm } from "@/screens/CompanySettings/Accounting/forms/PaymentTermsForm"
import type { VendorPayableFields } from "@/screens/CompanySettings/Accounting/hooks/useGetVendorPayableFields"
import { usePaymentTermsDialog } from "@/screens/CompanySettings/Accounting/hooks/usePaymentTermsDialog"
import type { PaymentTermsFormValues } from "@/screens/CompanySettings/Accounting/schemas/paymentTermsFormSchema"
import { paymentTermsFormSchema } from "@/screens/CompanySettings/Accounting/schemas/paymentTermsFormSchema"

interface CompanyAccountingTermsDialogProps {
  companyId: string
  vendorPayableFields: VendorPayableFields
  data?: CompanyAccountingTerm[]
  onClose: () => void
  onSubmit: () => void
}

const defaultFormValues: Omit<PaymentTermsFormValues, "companyId"> = {
  creditLimitCustomer: "",
  paymentTermIdCustomer: "",
  creditLimitVendor: "",
  paymentTermIdVendor: "",
  sensitiveAccount: null,
  defaultExpenseAccountId: null,
}

export const CompanyAccountingTermsDialog: FC<CompanyAccountingTermsDialogProps> = ({
  companyId,
  vendorPayableFields,
  data,
  onClose,
  onSubmit,
}) => {
  const isCreateMode = !data

  const methods = useForm<PaymentTermsFormValues>({
    defaultValues: isCreateMode
      ? {
          ...defaultFormValues,
          companyId,
        }
      : {
          companyId,
          creditLimitCustomer:
            data?.find((term) => term.companyType === COMPANY_TYPE.CUSTOMER)?.creditLimit?.toString() ?? "",
          paymentTermIdCustomer: data?.find((term) => term.companyType === COMPANY_TYPE.CUSTOMER)?.paymentTermId ?? "",
          creditLimitVendor:
            data?.find((term) => term.companyType === COMPANY_TYPE.VENDOR)?.creditLimit?.toString() ?? "",
          paymentTermIdVendor: data?.find((term) => term.companyType === COMPANY_TYPE.VENDOR)?.paymentTermId ?? "",
          sensitiveAccount: vendorPayableFields.sensitiveAccount,
          defaultExpenseAccountId: vendorPayableFields.defaultExpenseAccountId,
        },
    resolver: zodResolver(paymentTermsFormSchema),
    mode: "onChange",
  })

  const { setIsCancelConfirmationOpen, isCancelConfirmationOpen, canSubmit, handleSubmit, handleCancel } =
    usePaymentTermsDialog({
      companyId,
      methods,
      onSuccess: onSubmit,
      onClose,
    })

  return (
    <>
      {isCancelConfirmationOpen && (
        <CancelDialog onClose={() => setIsCancelConfirmationOpen(false)} onConfirm={onClose} />
      )}
      <Dialog
        open
        onClose={handleCancel}
        maxWidth='sm'
        fullWidth
        data-testid='registration-dialog'
        sx={{ display: isCancelConfirmationOpen ? "none" : "block" }}
      >
        <Box>
          <DialogHeader title='Payment and Credit Terms' onClose={handleCancel} />
          <DialogContent classes={{ root: "p-10" }}>
            <FormProvider {...methods}>
              <PaymentTermsForm onSubmit={handleSubmit} methods={methods} data={data} />
            </FormProvider>
          </DialogContent>
          <DialogActions className='justify-between px-10 pb-10'>
            <Button appearance='outlined' onClick={handleCancel} data-testid='cancel-button'>
              Cancel
            </Button>
            <Button
              appearance='contained'
              type='button'
              disabled={!canSubmit}
              onClick={handleSubmit}
              data-testid='submit-button'
            >
              Save
            </Button>
          </DialogActions>
        </Box>
      </Dialog>
    </>
  )
}
